import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { setAuthToken } from '../lib/auth';
import { SummarosLockup } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POST_LOGIN_KEY = 'summaros:post-login-next';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Sign-in link expired. Try again.');
          navigate('/login');
          return;
        }

        const response = await axios.post(
          `${API}/auth/session`,
          {},
          {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true,
          },
        );

        const user = response.data;
        if (user?.session_token) setAuthToken(user.session_token);

        // Onboarding routing:
        //   New user OR user without an assessment → /welcome
        //   Explicit ?next= override wins if present
        let next = null;
        try {
          const stored = sessionStorage.getItem(POST_LOGIN_KEY);
          if (stored && stored.startsWith('/')) next = stored;
          sessionStorage.removeItem(POST_LOGIN_KEY);
        } catch { /* ignore */ }

        if (!next) {
          next = user?.has_completed_assessment ? '/my-life' : '/welcome';
        }

        toast.success(`Welcome${user?.name ? `, ${user.name.split(' ')[0]}` : ''}.`);
        navigate(next, { state: { user }, replace: true });
      } catch (error) {
        console.error('Auth failed:', error);
        toast.error('Sign-in failed. Try again.');
        navigate('/login');
      }
    };

    processSession();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <div className="text-center animate-fade-in">
        <div className="inline-flex items-center justify-center mb-6">
          <SummarosLockup width={180} testid="authcallback-lockup" />
        </div>
        <div className="inline-flex items-center gap-2 text-ink-muted">
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
          <span className="text-sm">Signing you in…</span>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
